import { plural, palavraNoPlural } from '@/utils/plural';
import { useCuradoriaAbertos, classeDoNumeroDaCuradoria } from '@/features/quiz/api/curadoriaAbertos';
import { GRUPOS_DE_RELATORIO_DA_GESTAO } from '@/features/teacher/relatorios/gruposDeRelatorio';
import { ITEM_DO_RODAPE_DA_GAVETA } from '@/components/layout/itemDaGaveta';
import { Link, useLocation } from 'react-router-dom';
import { SinoDeNotificacoes } from '@/features/notificacoes/SinoDeNotificacoes';
import SairDaConta from '@/components/layout/SairDaConta';
import { BotaoDeRelato } from '@/features/relatos/components/RelatarAlgo';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useMenuRecolhido } from '@/components/layout/menuRecolhido';
import {
  HomeIcon, UserGroupIcon, AcademicCapIcon, EnvelopeIcon, ClipboardDocumentListIcon, ChartBarIcon,
  QuestionMarkCircleIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

/**
 * Navegação da equipe gestora.
 *
 * NÃO reusa o menu do professor de propósito: os onze itens dele incluem
 * Disciplinas, Conteúdos e nove relatórios, e nada disso é da gestão. O menu
 * foi pensado para crescer por acréscimo, não por rearranjo.
 *
 * Reusar a TELA é outra coisa, e é o que se faz: Questões aponta para a mesma
 * `QuestionsPage` do professor. O que muda entre os dois é o que a página
 * OFERECE, decidido pelo papel em `features/quiz/permissoes.ts` — não a
 * página.
 *
 * Enquanto a escola aguarda revisão, só o Início aparece. Não itens
 * desabilitados: itens que somem. Botão cinza convida a clicar e a descobrir por
 * tentativa; ausência mais explicação, não.
 *
 * Simulados entrou na fatia 5: a gestão administra os da escola e acompanha os
 * dos professores da casa. Entrou como item novo, sem rearranjo — que era o que
 * o menu tinha sido pensado para permitir.
 */
const SECOES_DA_GAVETA = [
  // "Turmas e alunos" (26/10/2026): as duas listas numa entrada, em abas — a mesma reorganização do menu do professor.
  { titulo: 'Dia a dia', caminhos: ['/gestao', '/gestao/simulados', '/gestao/relatorios', '/gestao/turmas-e-alunos'] },
  { titulo: 'A escola', caminhos: ['/gestao/professores', '/gestao/convites'] },
  { titulo: 'Acervo', caminhos: ['/gestao/questoes', '/gestao/curadoria'] },
];

const ITENS: Array<{ path: string; label: string; icon: typeof HomeIcon; sempre?: boolean; tambem?: string[] }> = [
  { path: '/gestao', label: 'Painel', icon: HomeIcon, sempre: true },
  // Acende também na página da turma e na do aluno, e nas importações (`/gestao/turmas/…`, `/gestao/alunos/…`).
  { path: '/gestao/turmas-e-alunos', label: 'Turmas e alunos', icon: UserGroupIcon, tambem: ['/gestao/turmas', '/gestao/alunos'] },
  { path: '/gestao/professores', label: 'Professores', icon: AcademicCapIcon },
  { path: '/gestao/convites', label: 'Convites', icon: EnvelopeIcon },
  /*
   * Questoes entrou em 01/09/2026, ANTES de Simulados e depois de Alunos.
   *
   * A razao e a ordem do trabalho, nao a estetica. O menu ja esta agrupado em
   * tres blocos: cadastro (Turmas, Professores, Convites, Alunos), instrumento
   * (Questoes, Simulados) e resultado (Relatorios). A gestao vai a Questoes
   * PARA montar o simulado — Questoes e o insumo do item seguinte, e po-la
   * depois inverteria a ordem em que o trabalho acontece.
   *
   * E e a mesma vizinhanca do menu do professor, com o mesmo rotulo e o mesmo
   * icone. A gestao conversa com os professores da casa; vocabulario igual nao
   * custa nada e evita traducao.
   *
   * Item NOVO, sem rearranjo: nenhum dos sete mudou de lugar, como este menu
   * foi pensado para crescer.
   */
  { path: '/gestao/questoes', label: 'Questões', icon: QuestionMarkCircleIcon },
  { path: '/gestao/simulados', label: 'Simulados', icon: ClipboardDocumentListIcon },
  { path: '/gestao/relatorios', label: 'Relatórios', icon: ChartBarIcon },
  // Item NOVO, sem rearranjo — como os anteriores. A fila de curadoria do
  // acervo: o que um professor reportou e o que a regra detectou.
  { path: '/gestao/curadoria', label: 'Curadoria', icon: ExclamationTriangleIcon },
];

/**
 * `variant` entrou para esta barra ganhar o mesmo rodapé de Sair das outras
 * duas — e pelo mesmo desenho: só na GAVETA, porque no desktop a barra do topo
 * do `MainLayout` já traz o botão e os dois apareceriam juntos.
 *
 * Na gaveta a barra passa a rolar POR DENTRO (`h-full` na raiz, `overflow-y-auto`
 * no `<nav>`), como no professor e no administrador. Antes quem rolava era o
 * invólucro do `GestaoLayout`; se continuasse assim, o rodapé desceria com a
 * lista e sumiria — o achado 96 em escala menor.
 */
const GestaoSidebar = ({
  podeAdministrar = true,
  convitesPendentes = 0,
  professoresSemTurma = 0,
  onNavigate,
  variant = 'sidebar',
}: {
  podeAdministrar?: boolean;
  convitesPendentes?: number;
  /** Entrou na escola e nao declarou turma nenhuma. Ver o achado 165. */
  professoresSemTurma?: number;
  onNavigate?: () => void;
  variant?: 'sidebar' | 'drawer';
}) => {
  const location = useLocation();
  const ativo = (p: string) => {
    if (p === '/gestao') return location.pathname === p;
    const tambem = ITENS.find((i) => i.path === p)?.tambem ?? [];
    return [p, ...tambem].some((c) => location.pathname === c || location.pathname.startsWith(`${c}/`));
  };
  const curadoriaAbertos = useCuradoriaAbertos(podeAdministrar);

  /**
   * Submenu de Relatórios. O Mapa de Descritores é o PRIMEIRO, e é o inverso da
   * ordem do professor: ele já é uma matriz (descritor × turma), e a gestão é a
   * única que tem mais de uma turma para comparar.
   *
   * Ficam de fora "Próximo Simulado" — planejamento de quem leciona — e "Por
   * Aluno", que é a conversa do professor com o aluno dele.
   */
  const relatorios = GRUPOS_DE_RELATORIO_DA_GESTAO.flatMap((g) => g.itens);
  const emRelatorios = location.pathname.startsWith('/gestao/relatorios');
  const visiveis = ITENS.filter((i) => podeAdministrar || i.sempre);

  const ehGaveta = variant === 'drawer';
  // Recolhida só a barra FIXA: a gaveta do celular nunca vira faixa de ícones (ver `menuRecolhido`).
  // O botão que recolhe fica no cabeçalho, em `GestaoLayout`.
  const recolhido = useMenuRecolhido() && !ehGaveta;
  const classeDoItem = (ativoAgora: boolean) =>
    `relative flex min-h-[44px] items-center rounded-md py-2 text-sm ${recolhido ? 'justify-center px-0' : 'gap-3 px-2'} ${
      ativoAgora ? 'bg-marca-50 font-medium text-marca-700' : 'text-gray-700 hover:bg-gray-50'
    }`;
  return (
    // Na gaveta, uma lista só que rola inteira, com o rodapé no fim — ver a nota em `TeacherSidebar` (24/10/2026).
    <div className={ehGaveta ? 'h-full min-h-0 flex flex-col overflow-y-auto overscroll-contain' : 'flex flex-col'}>
    <nav className={`flex flex-col gap-1 ${recolhido ? 'p-2' : 'px-2 py-4'} ${ehGaveta ? 'flex-none' : ''}`}>
      <div className={recolhido || ehGaveta ? 'hidden' : 'px-2 pb-3 mb-2 border-b border-gray-200'}>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Equipe gestora</p>
      </div>
      {/* NA GAVETA, EM SEÇÕES (23/10/2026, lote da gestão — o mesmo desenho da gaveta do professor): Dia a dia, A escola,
          Acervo, com título pequeno; "Relatórios" é um item só, que abre a página de relatórios. A barra lateral do
          computador segue com a lista e o submenu de sempre. */}
      {/* Desde 24/10/2026 a barra do COMPUTADOR também é em seções (revisão do pesquisador); recolhida, a faixa de ícones de
          sempre (com o submenu dos relatórios ao lado). */}
      {podeAdministrar && !recolhido ? (
        <div data-testid={ehGaveta ? 'gaveta-em-secoes' : 'menu-em-secoes'} className="flex flex-col gap-1">
          {SECOES_DA_GAVETA.map((secao) => (
            <div key={secao.titulo} className="mb-2">
              <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{secao.titulo}</p>
              {secao.caminhos.map((caminho) => {
                const item = ITENS.find((x) => x.path === caminho)!;
                const Icon = item.icon;
                const selo = caminho === '/gestao/convites' ? convitesPendentes : caminho === '/gestao/professores' ? professoresSemTurma : 0;
                return (
                  <Link key={caminho} to={caminho} onClick={onNavigate} className={`${classeDoItem(caminho === '/gestao/relatorios' ? emRelatorios : ativo(caminho))} min-h-[44px]`} aria-current={(caminho === '/gestao/relatorios' ? emRelatorios : ativo(caminho)) ? 'page' : undefined} data-testid={`menu-${caminho}`}>
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {selo > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">{caminho === '/gestao/convites' ? plural(selo, 'pendente', 'pendentes') : `${selo} sem turma`}</span>}
                    {caminho === '/gestao/curadoria' && curadoriaAbertos > 0 && (
                      <span className={classeDoNumeroDaCuradoria(false)} data-testid="numero-da-curadoria">{curadoriaAbertos}<span className="sr-only"> {palavraNoPlural(curadoriaAbertos, 'achado aberto', 'achados abertos')}</span></span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      ) : visiveis.map(({ path, label, icon: Icon }) => (
        // RECOLHIDA, "Relatórios" abre os nove ao lado: o submenu de sempre mora DENTRO da barra,
        // abaixo do item, e numa faixa de 64 px não há onde escrevê-lo. Sem isto, quem recolhesse
        // o menu perdia o caminho de um relatório para o outro.
        recolhido && path === '/gestao/relatorios' ? (
          <Popover key={path}>
            <PopoverButton title={label} className={`w-full focus:outline-none ${classeDoItem(ativo(path))}`}>
              <Icon className="h-5 w-5 shrink-0" />
              <span className="sr-only">{label}</span>
            </PopoverButton>
            <PopoverPanel anchor={{ to: 'right start', gap: 8 }} className="z-50 w-60 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
              {({ close }) => (
                <>
                  {relatorios.map((r) => (
                    <Link key={r.path} to={r.path} onClick={() => { close(); onNavigate?.(); }}
                      className={`block rounded px-2 py-2 text-sm ${location.pathname === r.path ? 'bg-marca-50 font-medium text-marca-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                      {r.label}
                    </Link>
                  ))}
                </>
              )}
            </PopoverPanel>
          </Popover>
        ) : (
        <Link
          key={path}
          to={path}
          onClick={onNavigate}
          title={recolhido ? label : undefined}
          className={classeDoItem(ativo(path))}
        >
          <Icon className="h-5 w-5 shrink-0" />
          <span className={recolhido ? 'sr-only' : 'flex-1'}>{label}</span>
          {/*
            OS SELOS marcam ATO PENDENTE da gestao, e ficam no item onde o ato
            e feito — nao num painel de avisos separado, que seria mais uma tela
            a consultar e ninguem consulta.

            ERAM TRES. `simuladosSemLeitor` saiu em 10/09/2026, no dia seguinte
            ao que entrou: a particao do alcance fez o professor da escola
            acompanhar o simulado dela por vinculo, e "nenhum leitor listado"
            deixou de ser pendencia — virou o estado normal. Selo que acende
            sempre nao informa; ensina a ignorar selo.
          */}
          {path === '/gestao/convites' && convitesPendentes > 0 && (
            <span className={recolhido ? 'absolute right-2 top-1.5 h-2 w-2 rounded-full bg-amber-500' : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'}
              title={recolhido ? plural(convitesPendentes, 'convite pendente', 'convites pendentes') : undefined}>
              {/* O número diz o que é (23/10/2026): um "3" sozinho não se lia. */}
              {recolhido ? <span className="sr-only">{plural(convitesPendentes, 'convite pendente', 'convites pendentes')}</span> : plural(convitesPendentes, 'pendente', 'pendentes')}
            </span>
          )}
          {path === '/gestao/curadoria' && curadoriaAbertos > 0 && (
            <span className={classeDoNumeroDaCuradoria(recolhido)} data-testid="numero-da-curadoria" title={recolhido ? plural(curadoriaAbertos, 'achado aberto', 'achados abertos') : undefined}>
              {recolhido ? <span className="sr-only">{plural(curadoriaAbertos, 'achado aberto', 'achados abertos')}</span> : curadoriaAbertos}
            </span>
          )}
          {path === '/gestao/professores' && professoresSemTurma > 0 && (
            <span
              title={`${plural(professoresSemTurma, 'professor entrou', 'professores entraram')} e ainda não ${professoresSemTurma === 1 ? 'declarou' : 'declararam'} em que turma leciona. Você pode declarar.`}
              className={recolhido ? 'absolute right-2 top-1.5 h-2 w-2 rounded-full bg-amber-500' : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'}
            >
              {recolhido ? <span className="sr-only">{plural(professoresSemTurma, 'sem turma', 'sem turma')}</span> : `${professoresSemTurma} sem turma`}
            </span>
          )}
        </Link>
        )
      ))}

      {podeAdministrar && emRelatorios && !recolhido && !ehGaveta && (
        <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-gray-200 pl-3">
          {relatorios.map((r) => (
            <Link
              key={r.path}
              to={r.path}
              onClick={onNavigate}
              className={`rounded px-2 py-1 text-xs ${
                location.pathname === r.path
                  ? 'font-medium text-marca-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {r.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
      {/* Irmão do `<nav>` que rola — nunca filho. */}
      {/*
                O CANAL DE RELATO NO CELULAR.
                A barra do topo do `MainLayout` traz o botão, mas ela é
                `hidden md:flex` — abaixo de 768px ele some. E a gaveta do
                `MainLayout` não abre aqui, porque esta rota tem gaveta
                própria (`temGavetaPropria`). Sem esta linha, o professor no
                aplicativo ficava SEM canal em toda a área dele, que é onde
                ele passa o tempo todo.
            */}
      {ehGaveta && (
        <div className="flex-shrink-0 px-2">
          <SinoDeNotificacoes variante="item" aoNavegar={onNavigate} />
        </div>
      )}
      {ehGaveta && (
        <div className="flex-shrink-0 px-2 pb-1">
          <BotaoDeRelato className={ITEM_DO_RODAPE_DA_GAVETA} />
        </div>
      )}
      {ehGaveta && <SairDaConta aoSair={onNavigate} />}
    </div>
  );
};

export default GestaoSidebar;
